class ArmourbuyWhatsappContact extends HTMLElement {
  #account;
  #connected = false;
  #connectionToken = 0;
  #footerObserver;
  #footerTargetsVisible = new Set();

  connectedCallback() {
    if (this.#connected) return;
    this.#connected = true;
    const token = ++this.#connectionToken;

    this.#account = document.querySelector('shopify-account');
    this.#account?.addEventListener('open', this.#handleAccountOpen);
    this.#account?.addEventListener('close', this.#handleAccountClose);
    document.addEventListener('visitorConsentCollected', this.#syncPrivacyState);
    this.#setupFooterVisibility();

    const featureLoader = window.Shopify?.loadFeatures;
    if (typeof featureLoader !== 'function') {
      this.#usePrivacyFallback(new Error('Customer Privacy feature loader unavailable'));
      return;
    }

    try {
      featureLoader.call(
        window.Shopify,
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (!this.#connected || token !== this.#connectionToken) return;
          if (error) {
            this.#usePrivacyFallback(error);
            return;
          }
          this.#syncPrivacyState();
        },
      );
    } catch (error) {
      this.#usePrivacyFallback(error);
    }
  }

  disconnectedCallback() {
    if (!this.#connected) return;
    this.#connected = false;
    this.#connectionToken += 1;
    this.#account?.removeEventListener('open', this.#handleAccountOpen);
    this.#account?.removeEventListener('close', this.#handleAccountClose);
    document.removeEventListener('visitorConsentCollected', this.#syncPrivacyState);
    this.#footerObserver?.disconnect();
    this.#footerObserver = undefined;
    this.#footerTargetsVisible.clear();
    this.#account = undefined;
    this.removeAttribute('data-account-sheet-open');
    this.removeAttribute('data-privacy-banner-open');
    this.removeAttribute('data-privacy-api-fallback');
    this.removeAttribute('data-footer-visible');
  }

  #handleAccountOpen = () => {
    this.setAttribute('data-account-sheet-open', '');
  };

  #handleAccountClose = () => {
    this.removeAttribute('data-account-sheet-open');
  };

  #syncPrivacyState = () => {
    try {
      const customerPrivacy = window.Shopify?.customerPrivacy;
      if (typeof customerPrivacy?.shouldShowBanner !== 'function') {
        throw new Error('Customer Privacy API unavailable');
      }
      this.removeAttribute('data-privacy-api-fallback');
      this.toggleAttribute(
        'data-privacy-banner-open',
        Boolean(customerPrivacy.shouldShowBanner()),
      );
    } catch (error) {
      this.#usePrivacyFallback(error);
    }
  };

  #setupFooterVisibility() {
    const targets = [
      document.querySelector('.ab-footer-directory'),
      document.querySelector('[data-testid="footer-utilities"]'),
    ].filter(Boolean);
    if (typeof IntersectionObserver !== 'function' || targets.length === 0) return;

    this.#footerTargetsVisible.clear();
    this.#footerObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#footerTargetsVisible.add(entry.target);
        else this.#footerTargetsVisible.delete(entry.target);
      }
      this.toggleAttribute('data-footer-visible', this.#footerTargetsVisible.size > 0);
    });
    for (const target of targets) this.#footerObserver.observe(target);
  }

  #usePrivacyFallback(error) {
    this.removeAttribute('data-privacy-banner-open');
    this.setAttribute('data-privacy-api-fallback', '');
    if (window.Shopify?.designMode) {
      console.warn('ARMOURBUY WhatsApp privacy visibility fallback', error);
    }
  }
}

if (!customElements.get('armourbuy-whatsapp-contact')) {
  customElements.define('armourbuy-whatsapp-contact', ArmourbuyWhatsappContact);
}
